import { Property } from '../property';
import { DimensionAssignment } from './models/dimension-assignment';
import { PropertyAssignment } from './models/property-assignment';

export interface MissingRequiredField {
  kind: 'PROPERTY' | 'DIMENSION';
  id: string;
  name: string;
  /** Parent object property name when this is a nested required field. */
  parentName?: string;
}

export function isMissingValue(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Returns required property/dimension assignments on the asset type that have no value
 * on the given submission. Newly added required fields on an existing asset type are
 * included — used to block checkout until they are filled in.
 */
export function getMissingRequiredFields(
  propertySet: {
    properties?: PropertyAssignment[];
    dimensions?: DimensionAssignment[];
  },
  propertyValues: Record<string, unknown> = {},
  dimensionValues: Record<string, unknown> = {}
): MissingRequiredField[] {
  const missing: MissingRequiredField[] = [];

  for (const assignment of propertySet.properties ?? []) {
    if (assignment.hidden || !assignment.required || !assignment.property) continue;
    collectMissingProperty(assignment.property, true, propertyValues[assignment.property.valueKey], missing);
  }

  for (const assignment of propertySet.dimensions ?? []) {
    if (assignment.hidden || !assignment.required) continue;
    if (isMissingValue(dimensionValues[assignment.dimensionId])) {
      missing.push({
        kind: 'DIMENSION',
        id: assignment.dimensionId,
        name: assignment.dimension?.title ?? assignment.dimensionId,
      });
    }
  }

  return missing;
}

function collectMissingProperty(
  property: Property.Model<Property.PropertyType>,
  required: boolean,
  value: unknown,
  missing: MissingRequiredField[],
  parentName?: string
): void {
  if (!required) {
    // Optional parent with no value — nested required children are not enforced.
    if (isMissingValue(value)) return;
  } else if (isMissingValue(value)) {
    missing.push({ kind: 'PROPERTY', id: property.id, name: property.name, parentName });
    return;
  }

  if (property.type !== Property.PropertyType.OBJECT) return;

  const objectValue = (value ?? {}) as Record<string, unknown>;
  for (const child of (property as Property.ObjectModel).properties ?? []) {
    if (child.hidden || !child.property) continue;
    const childRequired = required === false && isMissingValue(value) ? false : child.required;
    collectMissingProperty(child.property, childRequired, objectValue[child.property.valueKey], missing, property.name);
  }
}
